import React from "react";

import { IconMetrics } from "~/components/icon";
import { DashboardChartPluginSpec } from "~/main/common/dashboard-spec";
import type { Spec as ChartSpec } from "~/main/components/widget-form/spec";
import { MetricQuery } from "~/models/metric-query";
import { isError } from "~/tools/is-error";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";

import type { BoundMetric } from "../../../common/openapi";
import { queryComponentBoundMetric } from "../../../queries/components/metrics";
import { useQuery } from "../../../queries/react-query";
import { DrawerTitle, ExplorationDrawer } from "../../react/drawer/ExplorationDrawer";
import { AddToDashboardButton } from "../_add-to-dashboard-button";
import { Content } from "../_content";
import { PinComponentButton } from "../_pin-component-button";

import type { BoundMetricsDrawerState } from "./state";

interface Model extends Pick<BoundMetricsDrawerState, "title"> {
	metricQueries: ReadonlyArray<MetricQuery>;
	chart: ChartSpec;
}

function selectModel({ boundQueries, name, chart }: BoundMetric) {
	const spec = chart ? DashboardChartPluginSpec.fromOpenApi(chart) : undefined;
	return RemoteData.success({
		metricQueries: boundQueries.map(MetricQuery),
		title: name,
		chart: DashboardChartPluginSpec.toChartSpec(spec),
	} satisfies Model);
}

export interface BoundMetricsDrawerProps {
	when: undefined | BoundMetricsDrawerState;
}

export const BoundMetricsDrawer: React.FC<BoundMetricsDrawerProps> = ({ when }) => {
	const { error, data = RemoteData.loading() } = useQuery({
		enabled: when != null && when.boundMetricId != null,
		select: selectModel,

		...queryComponentBoundMetric({
			componentIdOrIdentifier: when?.componentIdOrIdentifier ?? "",
			boundMetricId: when?.boundMetricId ?? { _type: "BoundPresentationMetricId", metricId: "" },
			timestamp: when?.timestamp,
		}),
	});

	return (
		<ExplorationDrawer
			headerActions={({ componentIdOrIdentifier, timestamp }) =>
				error == null && (
					<>
						{componentIdOrIdentifier != null && (
							<PinComponentButton
								chart={data.transform(prop("chart")).getOrElse(undefined)}
								componentIdOrIdentifier={componentIdOrIdentifier}
								metricQueries={data.transform(prop("metricQueries")).getOrElse(undefined)}
								timestamp={timestamp}
								title={data.transform(prop("title")).getOrElse(undefined)}
							/>
						)}

						<AddToDashboardButton
							model={data.transform((current) => ({ ...current, componentIdOrIdentifier, timestamp }))}
						/>
					</>
				)
			}
			headerTitle={() => (
				<DrawerTitle
					icon={<IconMetrics height={20} width={20} />}
					title={error == null ? data.transform(prop("title")).getOrElse(undefined) : ""}
				/>
			)}
			when={when}
		>
			{({ timestamp, timeRange }) => (
				<Content
					model={isError(error) ? RemoteData.failure(error) : data}
					timeRange={timeRange}
					timestamp={timestamp}
				/>
			)}
		</ExplorationDrawer>
	);
};
